import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from './settingsService';
import { useAuth } from '../AuthContext';
import api from '../api';

const SettingsModal = ({ onClose }) => {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState({ email_whitelist: '' });
  const [backendError, setBackendError] = useState(false);

  useEffect(() => {
    setBackendError(false);
    getSettings()
      .then(settings => {
        setFormData({ email_whitelist: settings.email_whitelist || '' });
      })
      .catch(err => {
        console.error("Connectivity error:", err);
        setBackendError(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const success = await updateSettings(formData);
      if (success) {
        alert("Einstellungen gespeichert.");
        onClose();
      } else {
        alert("Speichern fehlgeschlagen.");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      alert(
        "Verbindung zum Backend nicht möglich.\n\n" +
        "Bitte sicherstellen, dass das Python-Backend auf http://127.0.0.1:8000 läuft."
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          ✕
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-800">Einstellungen</h2>

        {backendError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <strong className="font-bold">Backend-Fehler!</strong>
            <span className="block sm:inline"> Verbindung zum Backend nicht möglich.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-bold border-b pb-1">E-Mail-Monitor</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Whitelist
              </label>
              <textarea
                rows={4}
                placeholder="bastian@example.de, .*@hetzner.*\.de"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm font-mono"
                value={formData.email_whitelist}
                onChange={(e) => setFormData({...formData, email_whitelist: e.target.value})}
              />
              <p className="text-[10px] text-gray-500 mt-1">Kommagetrennt. Regex erlaubt (z.B. <code>.*@hetzner.*\.de</code>).</p>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition"
            >
              Speichern
            </button>
          </div>
        </form>

        {isAdmin && <UsersSection />}
      </div>
    </div>
  );
};

const UsersSection = () => {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const reload = () => api.get('/users').then(r => setUsers(r.data)).catch(() => {});

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const form = new FormData();
      form.append('email', newEmail);
      form.append('password', newPassword);
      form.append('role', newRole);
      await api.post('/users', form);
      setNewEmail(''); setNewPassword(''); setNewRole('user'); setShowForm(false);
      reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (u) => {
    const form = new FormData();
    form.append('is_active', String(!u.is_active));
    await api.put(`/users/${u.id}`, form);
    reload();
  };

  const handleReset = async (userId) => {
    if (!resetPassword.trim()) return;
    const form = new FormData();
    form.append('password', resetPassword);
    await api.put(`/users/${userId}`, form);
    setResetId(null); setResetPassword('');
    alert('Passwort zurückgesetzt.');
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    await api.delete(`/users/${userId}`);
    reload();
  };

  return (
    <div className="mt-6 space-y-4 border-t pt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-gray-800">Benutzer</h3>
        <button
          onClick={() => setShowForm(v => !v)}
          className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
        >
          + Neu
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="space-y-2 bg-gray-50 p-3 rounded border">
          <input
            type="email" required placeholder="E-Mail"
            className="w-full border rounded p-1.5 text-sm"
            value={newEmail} onChange={e => setNewEmail(e.target.value)}
          />
          <input
            type="password" required placeholder="Passwort" minLength={8}
            className="w-full border rounded p-1.5 text-sm"
            value={newPassword} onChange={e => setNewPassword(e.target.value)}
          />
          <select
            className="w-full border rounded p-1.5 text-sm"
            value={newRole} onChange={e => setNewRole(e.target.value)}
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <div className="flex gap-2">
            <button type="submit" disabled={adding}
              className="flex-1 bg-blue-600 text-white text-sm py-1.5 rounded hover:bg-blue-700 disabled:opacity-50">
              {adding ? 'Speichern…' : 'Anlegen'}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="flex-1 bg-gray-200 text-sm py-1.5 rounded hover:bg-gray-300">
              Abbrechen
            </button>
          </div>
        </form>
      )}

      <ul className="divide-y text-sm">
        {users.map(u => (
          <li key={u.id} className="py-2 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div>
                <span className={u.is_active ? '' : 'line-through text-gray-400'}>{u.email}</span>
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {u.role}
                </span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggleActive(u)}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  title={u.is_active ? 'Deaktivieren' : 'Aktivieren'}>
                  {u.is_active ? '⏸' : '▶'}
                </button>
                <button onClick={() => { setResetId(resetId === u.id ? null : u.id); setResetPassword(''); }}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Passwort zurücksetzen">
                  🔑
                </button>
                <button onClick={() => handleDelete(u.id)}
                  className="text-xs px-2 py-1 rounded bg-red-50 hover:bg-red-100 text-red-600" title="Löschen">
                  🗑
                </button>
              </div>
            </div>
            {resetId === u.id && (
              <div className="flex gap-2 mt-1">
                <input
                  type="password" placeholder="Neues Passwort" minLength={8}
                  className="flex-1 border rounded p-1 text-xs"
                  value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                />
                <button onClick={() => handleReset(u.id)}
                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
                  Setzen
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SettingsModal;
